import {
    Box,
    lighten,
    Menu,
    menuClasses,
    Stack,
    SxProps,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
    Typography,
} from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import { visuallyHidden } from '@mui/utils'
import { Features } from 'c1gen/features'
import { Permissions } from 'c1gen/permissions'
import { Roles } from 'c1gen/roles'
import { C1Button, C1IconButton } from 'c1ui'
import { C1CircularProgress } from 'c1ui/components/C1CircularProgress'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { skeletonFadeInSx } from 'c1ui/theme/C1Transitions'
import { EmptyList } from 'components/core/empty-list/index'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { CustomColumnConfigurationUI } from 'components/core/list/C1Table/CustomColumnConfigurationUI'
import { useGetTableCheckboxColumn } from 'components/core/list/C1Table/useGetTableCheckboxColumn'
import { TableHeaderStyles } from 'components/core/list/styles'
import { C1TablePagination } from 'components/core/pagination/C1TablePagination'
import { useFeatureCheckerLazy, usePermissionCheckerLazy, useRoleCheckerLazy } from 'components/no-ui/capability'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { ConnectorAccuracyStatus } from 'components/pages/accessreview/details/campaign_accuracy/getConnectorAccuracyStatus'
import { useLocalStorage } from 'hooks/local-storage'
import useGetDefaultPageSize from 'hooks/useGetDefaultPageSize'
import { TableErrorFallback } from 'lib/error-fallback'
import { ChevronDown, ChevronUp, CircleAlert, Columns3, RefreshCw } from 'lucide-react'
import { usePathname } from 'next/navigation'
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { FormattedMessage, useIntl } from 'react-intl'
import { SerializedErrorType } from 'redux_toolkit/lib/reducer'
import { C1TableReduxStyles } from './C1TableReduxStyles'
import { useTableFocusManagement } from './useTableFocusManagement'
import { useTableKeyboardNavigation } from './useTableKeyboardNavigation'

export type SortDirectionType = 'asc' | 'desc'

export type C1TableCellProps<T, ExtraProps> = {
    row: T;
    extras: ExtraProps;
    toggleRowIsExpanded: () => void;
    rowIsExpanded: boolean;
    rowIsSelected: boolean;
}

export type C1TableGroupHeaderRow<T> = {
    id: string
    groupKey: string
    groupLabel: React.ReactNode | null
    rows: T[]
}

export type C1TableReduxColumn<T, ExtraProps = object> = {
    field: string
    label?: React.ReactNode | FC<{ datas: ReadonlyArray<T>; extras: ExtraProps }>
    alignmentOverrides?: 'margin1' | 'margin2'
    align?: 'left' | 'right' // default left
    Component: FC<C1TableCellProps<T, ExtraProps>>
    shouldRender?: (hasFeature: (feature: Features) => boolean | null,
        hasPermissions: (permission: Permissions) => boolean | null,
        hasRoles: (role: Roles) => boolean | null,
        extras: ExtraProps) => boolean
    onSort?: (extras: ExtraProps, direction: SortDirectionType) => void
    isSticky?: (extras: ExtraProps) => boolean
    minWidth?: string | ((extras: ExtraProps) => string)
    maxWidth?: string | ((extras: ExtraProps) => string)
    width?: string | ((extras: ExtraProps) => string)
    flex?: number
    isError?: (row: T) => boolean
    loadingElement?: React.ReactNode
    status?: (row: T) => number
    isLocked?: boolean
}

type GetId<T> = (row: T, i?: number) => string
export type C1TableVariant = 'default' | 'minimal'

/**
 * @designSystem
 */
export type C1TableReduxProps<T, ExtraProps> = {
    /** Whether more pages are available beyond what has been fetched. Enables the "next page" pagination button. */
    canLoadMore?: boolean
    /** Column definitions that control header labels, cell rendering, sorting, sizing, and conditional visibility. */
    cols: C1TableReduxColumn<T, ExtraProps>[]
    /** Pre-paginated row data. Outer array is pages, inner array is rows for that page. Matches the shape from Redux search API hooks. */
    data: ReadonlyArray<ReadonlyArray<T>>
    /** Pre-paginated grouped row data. When provided, rows render under group header labels. Each group contains an id, label, and its rows. */
    groupedData?: ReadonlyArray<ReadonlyArray<C1TableGroupHeaderRow<T>>>
    /** Column `field` key to sort by on initial render. Highlights the sort indicator on that column header. */
    defaultSortByColumn?: string
    /** Custom empty-state content shown when there are no rows. Defaults to a generic `EmptyList` illustration. */
    emptyState?: React.ReactNode
    /** Serialized API error object. When provided alongside `isError`, renders an `APIError` banner below the table with the message and status code. */
    error?: SerializedErrorType
    /** Extracts a unique string key from each row for React reconciliation and selection tracking. Defaults to `row.id + index`. */
    getId?: GetId<T>
    /** Returns `true` for rows that should display an error indicator (red left-border accent on the first cell). */
    isRowError?: (row: T) => boolean
    /** Whether the data fetch failed. Shows an error illustration in the table body and an error border on the container. */
    isError: boolean
    /** Whether the initial data fetch is in progress. Shows skeleton rows as placeholders. */
    isLoading: boolean
    /** Whether a background page fetch is happening (e.g. prefetching the next page). Shows a small spinner next to the pagination controls. */
    isBackgroundLoading?: boolean
    /** Callback to fetch a specific page of data. If omitted, pagination controls are hidden. */
    onPageChange?: (pageNumber: number) => Promise<void>
    /** Zero-based index of the currently displayed page in the `data` array. */
    page: number
    /** Number of rows per page. Controls how many skeleton rows to show while loading. Falls back to the user's default page size preference. */
    pageSize?: number
    /** Total number of rows across all pages (estimated). Used by the pagination display. */
    totalRowCount?: number
    /** Extra props bag threaded to every column's `Component`, `shouldRender`, `onSort`, `isSticky`, and sizing functions. Useful for passing shared context like callbacks or feature flags. */
    staticExtraProps?: ExtraProps
    /** When `true`, shows a banner prompting the user to refresh the list (e.g. after a background data change). */
    shouldReload?: boolean
    /** Callback invoked when the user clicks the refresh button in the reload banner. */
    onReload?: () => void
    /** Custom metadata element rendered in the header area above the table (e.g. filter chips, result counts). */
    tableMetaData?: React.ReactElement
    /** Initial sort direction applied to the `defaultSortByColumn`. Defaults to `'desc'`. */
    defaultSortDirection?: SortDirectionType
    /** Custom footer element that replaces the default selection count and "Clear selections" control. */
    extraFooterActions?: React.ReactElement
    /** Currently selected rows. Enables the checkbox column and selection footer when provided with `setSelections`. */
    selections?: T[]
    /** Rows that should appear checked but be non-interactive (greyed-out checkboxes). */
    disabledSelections?: T[]
    /** Callback to update the set of selected rows. Required alongside `selections` to enable row selection. */
    setSelections?: (selections: T[]) => void
    /** Override for the header "select all" checkbox click behavior. By default, toggles all rows on the current page. */
    onHeaderCheckboxClick?: (event: React.ChangeEvent<HTMLInputElement>) => void
    /** When `true`, indicates that selection is driven by a filter rather than individual checkboxes (changes the header checkbox to indeterminate). */
    isSelectingViaFilter?: boolean
    /** Component rendered as an expandable detail panel below a row when toggled open. */
    ExpandedContent?: FC<C1TableCellProps<T, ExtraProps>>
    /** Callback when a row is clicked or activated via keyboard. Not triggered for clicks on buttons, links, or inputs within the row. */
    onRowClick?: (event: React.MouseEvent | React.KeyboardEvent, row: T, extraProps: ExtraProps) => void
    /** Exact total count when known. Shown in the pagination display (e.g. "1-25 of 142"). */
    totalCount?: number
    /** Enables the column configuration menu (gear icon in the last header cell) for show/hide/reorder of columns. */
    columnConfigurationIsActive?: boolean
    /** Custom localStorage key for persisting column configuration. Defaults to the current pathname. Use when multiple tables share a page. */
    columnConfigurationKey?: string
    /** Full set of available columns for the configuration menu. Defaults to `cols`. Useful when some columns are hidden by default but available to add. */
    availableColumns?: C1TableReduxColumn<T, ExtraProps>[]
    /** Visual variant. `'default'` shows bordered container with pagination. `'minimal'` removes borders, shadow, and pagination for embedding inside other containers. */
    variant?: C1TableVariant
    /** Generates a custom `aria-label` for each row's keyboard focus anchor. Defaults to "Row {index}". */
    getRowAriaLabel?: (row: T) => string
}

type C1TableBody<T, ExtraProps> = {
    rows?: ReadonlyArray<T>
    groupedRows?: ReadonlyArray<C1TableGroupHeaderRow<T>>
    getId: GetId<T>
    staticExtraProps: ExtraProps
    isRowError: (row: T) => boolean
    emptyState: React.ReactNode
    selectionsAsMap?: Map<string, T>
    focusAnchorRefs?: React.MutableRefObject<Map<number, HTMLButtonElement>>
    initialFocusedRowIndex?: number
} & Pick<C1TableReduxProps<T, ExtraProps>, 'cols' | 'isError' | 'isLoading' | 'pageSize' | 'ExpandedContent' | 'onRowClick' | 'getRowAriaLabel'>

const defaultFocusAnchorRefs = { current: new Map<number, HTMLButtonElement>() }

export const C1TableBody = <T, ExtraProps>({
    cols,
    rows,
    groupedRows,
    isRowError,
    emptyState,
    isError,
    isLoading,
    getId,
    staticExtraProps,
    ExpandedContent,
    onRowClick,
    selectionsAsMap,
    focusAnchorRefs = defaultFocusAnchorRefs,
    initialFocusedRowIndex = 0,
    getRowAriaLabel,
}: C1TableBody<T, ExtraProps>): React.ReactElement => {
    const hasFeature = useFeatureCheckerLazy()
    const hasPermissions = usePermissionCheckerLazy()
    const hasRoles = useRoleCheckerLazy()
    const filteredCols = cols.filter(
        (c) => (c.shouldRender ? c.shouldRender(hasFeature, hasPermissions, hasRoles, staticExtraProps) : true),
    )
    const numOfCols = filteredCols.length

    const [expandedRowsMap, setExpandedRowsMap] = useState<Set<string>>(new Set())

    const toggleRowIsExpanded = (id: string): void => {
        const newSet = new Set(expandedRowsMap)
        if (newSet.has(id)) {
            newSet.delete(id)
        } else {
            newSet.add(id)
        }
        setExpandedRowsMap(newSet)
    }

    const renderRow = (row: T, rowIndex: number): React.ReactElement => {
        const rowId = getId(row, rowIndex)
        const isExpanded = expandedRowsMap.has(rowId)
        const isSelected = selectionsAsMap?.has(rowId) ?? false

        return <C1TableRow
            key={rowId}
            cols={filteredCols}
            row={row}
            staticExtraProps={staticExtraProps}
            isRowError={isRowError}
            onClick={onRowClick}
            rowId={rowId}
            rowIndex={rowIndex}
            rowIsExpanded={isExpanded}
            toggleRowIsExpanded={(): void => toggleRowIsExpanded(rowId)}
            rowIsSelected={isSelected}
            ExpandedContent={ExpandedContent}
            initialTabIndex={initialFocusedRowIndex === rowIndex ? 0 : -1}
            focusAnchorRef={(el): void => {
                if (el) {
                    focusAnchorRefs.current.set(rowIndex, el)
                } else {
                    focusAnchorRefs.current.delete(rowIndex)
                }
            }}
            getRowAriaLabel={getRowAriaLabel}
        />
    }

    if (isError) {
        return <TableRow key="error">
            <Box sx={{ gridColumn: `1 / span ${numOfCols}` }}>
                <EmptyList
                    icon={<C1Icon icon={CircleAlert} />}
                    title={<FormattedMessage id="component_c1_table_error" defaultMessage="Something went wrong" />}
                    isError
                />
            </Box>
        </TableRow>
    }

    if (isLoading) {
        return <>
            {[...Array(5)].map((_, i) => (
                <TableRow key={`skeleton-row-${i}`} sx={skeletonFadeInSx}>
                    {filteredCols.map((column) => {
                        return (
                            <TableCell key={`skeleton-${column.field}`} sx={C1TableReduxStyles.skeletonRow}>
                                {column.loadingElement || <Skeleton sx={C1TableReduxStyles.skeleton} />}
                            </TableCell>
                        )
                    })}
                </TableRow>
            ))}
        </>
    }

    // Show empty state if no rows
    if ((!rows || rows.length === 0) && (!groupedRows || groupedRows.length === 0)) {
        return <TableRow>
            <TableCell key="empty-state" sx={{ gridColumn: `1 / span ${numOfCols}`, backgroundColor: 'background.default' }}>
                {emptyState}
            </TableCell>
        </TableRow>
    }

    // Render grouped rows if provided
    if (groupedRows?.length) {
        let globalRowIndex = 0
        return <>
            {groupedRows.map((group) => {
                const groupStartIndex = globalRowIndex
                globalRowIndex += group.rows.length
                return (
                    <React.Fragment key={group.id}>
                        {group.groupLabel && <Box
                            component="div"
                            sx={sxc(C1TableReduxStyles.groupHeader, {
                                gridColumn: '1 / -1',
                                borderBottom: '1px solid',
                                borderBottomColor: 'border.subtle',
                                backgroundColor: 'background.accent',
                            })}
                        >
                            {group.groupLabel}
                        </Box>}
                        {group.rows.map((row, localIndex) => renderRow(row, groupStartIndex + localIndex))}
                    </React.Fragment>
                )
            })}
        </>
    }

    // Render regular rows
    return <>{rows?.map(renderRow)}</>
}

const C1TableRow = <T, ExtraProps>({
    cols,
    row,
    staticExtraProps,
    isRowError,
    rowId,
    rowIndex,
    ExpandedContent,
    rowIsExpanded,
    rowIsSelected,
    toggleRowIsExpanded,
    onClick,
    initialTabIndex,
    focusAnchorRef,
    getRowAriaLabel,
}: {
    cols: C1TableReduxColumn<T, ExtraProps>[]
    row: T
    isRowError: (row: T) => boolean
    staticExtraProps: ExtraProps
    rowId: string
    rowIndex: number
    ExpandedContent?: FC<C1TableCellProps<T, ExtraProps>>
    rowIsExpanded: boolean
    rowIsSelected: boolean
    toggleRowIsExpanded: () => void
    onClick?: (event: React.MouseEvent | React.KeyboardEvent, row: T, extraProps: ExtraProps) => void
    initialTabIndex: number
    focusAnchorRef: (el: HTMLButtonElement | null) => void
    getRowAriaLabel?: (row: T) => string
}): React.ReactElement => {
    const intl = useIntl()
    const rowError = isRowError(row)

    const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>): void => {
        const targetElement = e.target as HTMLElement
        // if the click is not coming from a button, a, input, select, or a menu, then trigger the onClick
        // also ensure its coming from a table cell and not a sticky column
        const tableCell = targetElement.closest(`.${tableCellClasses.root}`)
        if (!targetElement.closest(`button, a, input, select, .${menuClasses.root} `) && tableCell && !tableCell.getAttribute('data-sticky')) {
            onClick && onClick(e, row, staticExtraProps)
        }
    }

    const handleFocusAnchorKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
        if ((e.key === 'Enter' || (e.key === ' ' && !e.shiftKey)) && onClick) {
            e.preventDefault()
            onClick(e, row, staticExtraProps)
        }
    }

    return (
        <>
            <TableRow
                key={rowId}
                data-dd-action-name={onClick ? 'Select row' : undefined}
                data-row-index={rowIndex}
                sx={{
                    '&:focus-within:has(.focus-anchor:focus)': {
                        [`& .${tableCellClasses.root}`]: (theme) => ({
                            position: 'relative',
                            boxShadow: `inset 0 -2px 0 0 ${theme.palette.primary.main}, inset 0 2px 0 0 ${theme.palette.primary.main}`,
                        }),
                        [`& .${tableCellClasses.root}:first-of-type:before, & .${tableCellClasses.root}:last-of-type:before`]: {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            width: '2px',
                            bottom: 0,
                            backgroundColor: 'primary.main',
                        },
                        [`& .${tableCellClasses.root}:last-of-type:before`]: {
                            right: 0,
                        },
                        [`& .${tableCellClasses.root}:first-of-type:before`]: {
                            left: 0,
                        },
                    },
                    '&:hover': {
                        [`& .${tableCellClasses.root}`]: onClick ? (theme) => ({
                            boxShadow: `0px -1px 0px ${theme.palette.primary.extraLight}`,
                            borderBottomColor: theme.palette.primary.extraLight,
                            backgroundColor: lighten(theme.palette.primary.extraLight, 0.5),
                            cursor: 'pointer',
                        }) : (theme) => ({
                            boxShadow: `0px -1px 0px ${theme.palette.grey[150]}`,
                            borderBottomColor: theme.palette.grey[150],
                            backgroundColor: theme.palette.grey[50],
                            cursor: 'default',
                        }),
                    },
                }}
                onClick={onClick ? (e): void => handleRowClick(e) : undefined}
            >
                {cols.map((column, columnIndex) => {
                    const isSticky = column.isSticky && column.isSticky(staticExtraProps)
                    const isStickyAndAlignRight = isSticky && column.align !== 'left'
                    return (
                        <TableCell
                            key={`${rowId}-${column.field}`}
                            data-sticky={isSticky || undefined}
                            sx={sxc(
                                [
                                    isSticky,
                                    C1TableReduxStyles.sticky,
                                ],
                                C1TableReduxStyles.cell,
                                { justifyContent: isStickyAndAlignRight || column.align === 'right' ? 'flex-end' : 'flex-start' },
                                [columnIndex === 0 && rowError, C1TableReduxStyles.errorCell],
                                [column.status?.(row) === ConnectorAccuracyStatus.ERROR, C1TableReduxStyles.errorCell],
                                [column.status?.(row) === ConnectorAccuracyStatus.WARNING, C1TableReduxStyles.warningCell],
                            )}
                        >
                            {/* Visually hidden focus anchor for keyboard navigation */}
                            {columnIndex === 0 && (
                                <Box sx={{ position: 'relative' }}>
                                    <Box
                                        component="button"
                                        className="focus-anchor"
                                        ref={focusAnchorRef}
                                        tabIndex={initialTabIndex}
                                        onKeyDown={handleFocusAnchorKeyDown}
                                        aria-label={getRowAriaLabel?.(row) ?? intl.formatMessage(
                                            { id: 'table_row_focus_anchor', defaultMessage: 'Row {index}' },
                                            { index: rowIndex + 1 },
                                        )}
                                        aria-expanded={ExpandedContent ? rowIsExpanded : undefined}
                                        aria-selected={rowIsSelected}
                                        sx={{ ...visuallyHidden }}
                                    />
                                </Box>
                            )}
                            <column.Component
                                row={row}
                                extras={staticExtraProps}
                                rowIsExpanded={rowIsExpanded}
                                toggleRowIsExpanded={toggleRowIsExpanded}
                                rowIsSelected={rowIsSelected}
                            />
                        </TableCell>
                    )
                })}
            </TableRow>
            {(rowIsExpanded && ExpandedContent) && (<TableRow>
                <TableCell
                    key={`${rowId}-expanded`}
                    sx={{
                        ...C1TableReduxStyles.cell,
                        gridColumn: `1 / span ${cols.length}`,
                        padding: 0,
                        '&:first-of-type': { paddingLeft: 0 },
                    }}
                >
                    <ExpandedContent
                        row={row}
                        extras={staticExtraProps}
                        rowIsExpanded={rowIsExpanded}
                        toggleRowIsExpanded={toggleRowIsExpanded}
                        rowIsSelected={rowIsSelected}
                    />
                </TableCell>
            </TableRow>)}

        </>
    )
}

const generateHash = async (str: string): Promise<string> => {
    const encoder = new TextEncoder()
    const data = encoder.encode(str)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
    return hashHex.substring(0, 8)
}

/**
 * Full-featured data table for paginated, sortable, selectable list views.
 *
 * C1TableRedux is the foundational table primitive used across the app for any list
 * that needs pagination, sorting, row selection, or expandable rows. It uses a
 * **CSS Grid layout** (not traditional HTML table layout) for flexible column sizing,
 * and is generic over both the row data type `T` and an `ExtraProps` bag that gets
 * threaded to every column renderer and callback.
 *
 * ## When to use
 * Reach for C1TableRedux when you need a table backed by a Redux search API
 * (or any paginated data source). Higher-level wrappers like `C1UsersList` or
 * `TaskListV2` compose this component internally — use those when they match your
 * use case to avoid re-implementing search, filtering, and column definitions.
 *
 * ## Variants
 * - `default` — bordered container with rounded corners, shadow, and a pagination
 *   footer. Use for standalone list pages.
 * - `minimal` — no border, no shadow, no pagination footer. Use when the table is
 *   embedded inside another container (e.g. a drawer, a tab panel, or a card).
 *
 * ## Data shape
 * Data is provided **pre-paginated** as `ReadonlyArray<ReadonlyArray<T>>` — an
 * outer array of pages, each containing an array of rows. The `page` prop indexes
 * into this outer array. This matches the shape returned by Redux search API hooks
 * (`userSearchApi.useSearch(...)`, etc.).
 *
 * ## Column definitions
 * Each column is a `C1TableReduxColumn<T, ExtraProps>` with a `field` key, a `label`,
 * and a `Component` that receives `{ row, extras, rowIsExpanded, toggleRowIsExpanded, rowIsSelected }`.
 * Columns support:
 * - **Sorting** — provide `onSort` to enable a clickable sort header
 * - **Sticky positioning** — `isSticky` pins a column to the right edge (useful for action menus)
 * - **Conditional rendering** — `shouldRender` gates on features, permissions, or roles
 * - **Flexible sizing** — `width`, `minWidth`, `maxWidth`, and `flex` control the CSS Grid track
 * - **Custom loading** — `loadingElement` replaces the default skeleton for that column
 *
 * ## Row selection
 * Pass `selections`, `setSelections`, and optionally `disabledSelections` to enable
 * checkbox-based row selection. A header checkbox is added automatically. The footer
 * shows a count and "Clear selections" button, or you can replace it with `extraFooterActions`.
 *
 * ## Expandable rows
 * Pass `ExpandedContent` to render an accordion-style detail panel below each row.
 * Column cell components receive `rowIsExpanded` and `toggleRowIsExpanded` to control state.
 *
 * ## Column configuration
 * Set `columnConfigurationIsActive` to enable a gear icon in the header that lets
 * users show/hide/reorder columns. Configuration is persisted in localStorage keyed
 * by the current pathname (or `columnConfigurationKey` if provided).
 *
 * ## Keyboard navigation
 * The table implements roving tabindex for row focus (Arrow Up/Down), Enter/Space
 * to activate `onRowClick`, and Shift+Arrow to extend selection. Screen reader
 * announcements are provided via `aria-live` regions.
 *
 * ## Error handling
 * The entire table is wrapped in an `ErrorBoundary`. When `isError` is `true`, the
 * body shows an error illustration. If `error` is provided, an `APIError` banner
 * renders below the table with the error message and code.
 *
 * @designSystem
 * @category molecules
 * @relatedComponent C1UsersList | Use instead when displaying a standard user list with search and pagination
 * @relatedComponent TaskListV2 | Use instead when displaying task lists with built-in search, filtering, and bulk actions
 * @externalReference MUI Table API | https://mui.com/material-ui/api/table/
 * @externalReference MUI TableSortLabel API | https://mui.com/material-ui/api/table-sort-label/
 * @externalReference react-error-boundary | https://github.com/bvaughn/react-error-boundary
 */
export const C1TableRedux = <T, ExtraProps = object>({
    canLoadMore,
    cols,
    data,
    groupedData,
    defaultSortByColumn = '',
    emptyState = <EmptyList />,
    error,
    getId = (x, i): string => `${(x as { id: string }).id}-${i}`,
    isRowError = (): boolean => false,
    isError,
    isLoading,
    isBackgroundLoading = false,
    onPageChange,
    page,
    pageSize: _pageSize,
    shouldReload,
    onReload,
    totalRowCount: _totalRowCount,
    tableMetaData,
    staticExtraProps: _staticExtraProps = {} as ExtraProps,
    defaultSortDirection = 'desc',
    ExpandedContent,
    selections,
    disabledSelections,
    setSelections,
    onHeaderCheckboxClick,
    isSelectingViaFilter,
    extraFooterActions,
    onRowClick,
    totalCount: _totalCount,
    columnConfigurationIsActive,
    columnConfigurationKey,
    availableColumns: _availableColumns,
    variant = 'default',
    getRowAriaLabel,
}: C1TableReduxProps<T, ExtraProps>): React.ReactElement => {
    const intl = useIntl()
    const staticExtraProps = useRef(_staticExtraProps).current
    const tableScrollingContainerRef = useRef<HTMLDivElement>(null)
    const tableBodyRef = useRef<HTMLTableSectionElement>(null)
    const hasFeature = useFeatureCheckerLazy()
    const hasPermissions = usePermissionCheckerLazy()
    const hasRoles = useRoleCheckerLazy()
    const [sortByColumn, setSortByColumn] = useState<string>(defaultSortByColumn)
    const [sortDirection, setSortDirection] = useState<SortDirectionType>(defaultSortDirection)
    const [columnCustomizerAnchorEl, setColumnCustomizerAnchorEl] = useState<null | HTMLElement>(null)

    const defaultPageSize = useGetDefaultPageSize()
    const pageSize = _pageSize ?? defaultPageSize

    const pathname = usePathname()
    const [tableConfigHash, setTableConfigHash] = useState<string>('')

    // Use availableColumns if provided, otherwise fall back to cols
    const availableColumns = _availableColumns || cols

    useEffect(() => {
        if (!columnConfigurationIsActive) {
            setTableConfigHash('')
            return
        }

        const columnFields = availableColumns.map((col) => col.field).join('|')
        const keyBase = columnConfigurationKey || pathname
        generateHash(keyBase + columnFields).then(setTableConfigHash)
    }, [columnConfigurationIsActive, columnConfigurationKey, pathname, availableColumns])

    const defaultColumnKeys = useMemo(() => cols.filter((col) => !col.isLocked).map((col) => col.field), [cols])

    // Get total row count for current page
    const currentPageRows = data ? data[page] : []
    const totalRowsOnPage = currentPageRows?.length ?? 0

    // This fixes going back to 0th page when we are on X page and do a filter or invalidate the list
    page = page + 1 > data?.length ? 0 : page

    const {
        focusedRowIndexRef,
        focusAnchorRefs,
        updateFocusedRow,
        handleTableFocus,
    } = useTableFocusManagement({ totalRowsOnPage, page })

    const { handleTableKeyDown } = useTableKeyboardNavigation({
        totalRowsOnPage,
        focusedRowIndexRef,
        updateFocusedRow,
        rowsOnPage: currentPageRows,
        selections,
        setSelections,
        disabledSelections,
        getId,
    })

    // When the page changes, scroll to the top of the table
    useEffect(() => {
        if (tableScrollingContainerRef.current) tableScrollingContainerRef.current.scrollTop = 0
    }, [page])

    // Use a single localStorage key for all table customizations
    const [allColConfigs, setAllColConfigs] = useLocalStorage<Record<string, string[]>>(
        'c1-table-column-customizations',
        {},
    )

    const handleColumnConfigChange = (newSelectedKeys: string[]): void => {
        setAllColConfigs({ ...allColConfigs, [tableConfigHash]: newSelectedKeys })
    }

    const handleResetColumnConfig = (): void => {
        const newCustomizations = { ...allColConfigs }
        delete newCustomizations[tableConfigHash]

        setAllColConfigs(newCustomizations)
        setColumnCustomizerAnchorEl(null)
    }

    const handleSort = useCallback(
        (c: C1TableReduxColumn<T, ExtraProps>): void => {
            let direction: SortDirectionType = 'desc'
            if (sortByColumn === c.field && sortDirection === 'desc') {
                direction = 'asc'
            }
            setSortByColumn(c.field)
            setSortDirection(direction)
            c.onSort?.(staticExtraProps, direction)
        },
        [staticExtraProps, sortByColumn, sortDirection],
    )

    // Helper function to check if a column should be rendered based on permissions
    const shouldRenderColumn = useCallback(
        (col: C1TableReduxColumn<T, ExtraProps>): boolean => (
            col.shouldRender ? col.shouldRender(hasFeature, hasPermissions, hasRoles, staticExtraProps) : true
        ),
        [hasFeature, hasPermissions, hasRoles, staticExtraProps],
    )

    const { column: checkboxColumn, selectionsAsMap } = useGetTableCheckboxColumn<T, ExtraProps>(
        selections,
        disabledSelections,
        setSelections,
        getId,
        data[page],
        onHeaderCheckboxClick,
        isSelectingViaFilter,
    )

    const canBeConfiguredCols = useMemo(() => availableColumns.filter(shouldRenderColumn), [availableColumns, shouldRenderColumn])

    const thisTableSavedColConfig = allColConfigs?.[tableConfigHash] || null

    const filteredCols = useMemo(() => {
        const colsToDisplay: C1TableReduxColumn<T, ExtraProps>[] = []

        const isCustomizationActive = columnConfigurationIsActive

        if (!isCustomizationActive || !thisTableSavedColConfig) {
            // If we aren't customizing, just filter based on shouldRender
            colsToDisplay.push(...(cols.filter(shouldRenderColumn)))
        } else {
            // Otherwise, we need to order the columns based on the saved configuration
            const lockedCols = canBeConfiguredCols.filter((c) => c.isLocked)
            const customizableCols = canBeConfiguredCols.filter((c) => !c.isLocked)

            const customizableColsMap = new Map(customizableCols.map((c) => [c.field, c]))

            const orderedCustomizableCols = thisTableSavedColConfig
                .map((fieldKey) => customizableColsMap.get(fieldKey))
                .filter((col): col is C1TableReduxColumn<T, ExtraProps> => col !== undefined)

            colsToDisplay.push(...orderedCustomizableCols, ...lockedCols)
        }

        if (checkboxColumn) colsToDisplay.unshift(checkboxColumn)

        return colsToDisplay
    }, [columnConfigurationIsActive,
        thisTableSavedColConfig, cols, shouldRenderColumn, canBeConfiguredCols,
        checkboxColumn])

    let grid = ''
    filteredCols.forEach((col) => {
        if (col.isSticky && col.isSticky(staticExtraProps)) {
            grid += ' max-content'
            return
        }

        const getValue = (val): string => ((val && typeof val === 'function') ? val(staticExtraProps) : val)

        if (col.maxWidth) {
            grid += ` ${getValue(col.maxWidth)}`
        } else if (col.minWidth) {
            grid += ` minmax(${getValue(col.minWidth)}, max-content)`
        } else if (col.width) {
            grid += ` minmax(${getValue(col.width)}, ${getValue(col.width)})`
        } else {
            grid += ` minmax(min-content, ${col.flex === undefined ? 1 : col.flex}fr)`
        }
    })

    const configureColumnsButton = useMemo(() => <Tooltip
        placement="bottom"
        title={intl.formatMessage({ id: 'component_table_column_customization_title', defaultMessage: 'Configure columns' })}
    >
        <C1IconButton
            id="column-customizer-button"
            aria-controls={columnCustomizerAnchorEl ? 'column-customizer-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={columnCustomizerAnchorEl ? 'true' : undefined}
            onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                setColumnCustomizerAnchorEl(event.currentTarget)
            }}
            aria-label={intl.formatMessage({ id: 'component_table_column_customization_title', defaultMessage: 'Configure columns' })}
            variant="text"
        >
            <C1Icon icon={Columns3} size="sm" />
        </C1IconButton>
    </Tooltip>, [columnCustomizerAnchorEl, intl])

    return <ErrorBoundary FallbackComponent={TableErrorFallback}>
        <TableContainer
            sx={sxc(
                C1TableReduxStyles.tableContainer,
                [!onPageChange || data?.[0]?.length === 0, { borderRadius: C1BorderRadius.large }],
                [isError, { borderColor: 'error.light' }],
                [variant === 'minimal', {
                    marginTop: 0,
                    borderRadius: 0,
                    boxShadow: 'none',
                    border: 'none',
                    borderTopWidth: 1,
                    borderTopStyle: 'solid',
                    borderTopColor: 'grey.150',
                }],
            )}
        >
            {(shouldReload || tableMetaData) && <Box sx={C1TableReduxStyles.reloadContainer}>
                {shouldReload && (
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography>
                            <FormattedMessage
                                id="component_c1_table_new_content"
                                defaultMessage="This list has been updated and needs to be refreshed."
                            />
                        </Typography>
                        <C1Button startIcon={<C1Icon icon={RefreshCw} />} onClick={onReload}>
                            <FormattedMessage id="component_c1_table_reload" defaultMessage="Refresh" />
                        </C1Button>
                    </Stack>
                )}
                {tableMetaData && <Box sx={C1TableReduxStyles.metadata}>{tableMetaData}</Box>}
            </Box>}
            <Box ref={tableScrollingContainerRef} sx={{ display: 'flex', flexDirection: 'column', overflow: 'auto', flexGrow: 1 }}>
                <Table
                    stickyHeader
                    size="small"
                    sx={sxc(C1TableReduxStyles.table, { gridTemplateColumns: grid.trim() })}
                >
                    <TableHead>
                        <TableRow>
                            {filteredCols.map((column, columnIndex) => {
                                const isSticky = column.isSticky && column.isSticky(staticExtraProps)
                                const isStickyAndAlignRight = isSticky && column.align !== 'left'
                                return (
                                    <TableCell
                                        key={column.field}
                                        scope="col"
                                        sx={sxc(
                                            [isSticky, C1TableReduxStyles.sticky],
                                        )}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isStickyAndAlignRight || column.align === 'right' ? 'flex-end' : 'flex-start', gap: 1, width: '100%' }}>
                                            <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center' }}>
                                                {column.onSort
                                                    ? <TableSortLabel
                                                        direction="desc"
                                                        onClick={(): void => handleSort(column)}
                                                        IconComponent={(): React.ReactElement => {
                                                            return sortDirection === 'desc' ? <C1Icon
                                                                icon={ChevronDown}
                                                                size="sm"
                                                                sx={sxc(C1TableReduxStyles.sortIcon, [
                                                                    sortByColumn === column.field,
                                                                    C1TableReduxStyles.sortIconActive,
                                                                ])}
                                                            /> : <C1Icon
                                                                icon={ChevronUp}
                                                                size="sm"
                                                                sx={sxc(C1TableReduxStyles.sortIcon, [
                                                                    sortByColumn === column.field,
                                                                    C1TableReduxStyles.sortIconActive,
                                                                ])}
                                                            />
                                                        }}
                                                    >
                                                        <Typography
                                                            component="span"
                                                            variant={variant === 'minimal' ? 'caption' : undefined}
                                                            sx={sxc(
                                                                { color: 'text.primary' },
                                                                [
                                                                    column.alignmentOverrides === 'margin1',
                                                                    TableHeaderStyles.margin1,
                                                                ],
                                                                [
                                                                    column.alignmentOverrides === 'margin2',
                                                                    TableHeaderStyles.margin2,
                                                                ],
                                                            )}
                                                        >
                                                            {
                                                                column.label && typeof column.label === 'function'
                                                                    ? <column.label datas={data[page]} extras={staticExtraProps} />
                                                                    : column.label
                                                            }
                                                        </Typography>
                                                        {sortByColumn === column.field ? (
                                                            <Box component="span" sx={{ ...visuallyHidden }}>
                                                                <FormattedMessage id="sorted" defaultMessage="sorted" />
                                                            </Box>
                                                        ) : null}
                                                    </TableSortLabel>
                                                    : <Typography
                                                        component="span"
                                                        variant={variant === 'minimal' ? 'caption' : undefined}
                                                        noWrap
                                                        sx={sxc(
                                                            { color: 'text.primary' },
                                                            [column.alignmentOverrides === 'margin1', TableHeaderStyles.margin1],
                                                            [column.alignmentOverrides === 'margin2', TableHeaderStyles.margin2],
                                                        )}
                                                    >
                                                        {
                                                            column.label && typeof column.label === 'function'
                                                                ? <column.label datas={data[page]} extras={staticExtraProps} />
                                                                : column.label
                                                        }
                                                    </Typography>
                                                }
                                            </Box>
                                            {(columnConfigurationIsActive && columnIndex === filteredCols.length - 1) &&
                                                configureColumnsButton}
                                        </Box>
                                    </TableCell>
                                )
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody ref={tableBodyRef} onKeyDown={handleTableKeyDown} onFocus={handleTableFocus}>
                        <C1TableBody
                            getId={getId}
                            cols={filteredCols}
                            rows={data ? data[page] : []}
                            groupedRows={groupedData ? groupedData[page] : undefined}
                            isError={isError}
                            isLoading={isLoading}
                            emptyState={emptyState}
                            pageSize={pageSize}
                            staticExtraProps={staticExtraProps}
                            isRowError={isRowError}
                            ExpandedContent={ExpandedContent}
                            onRowClick={onRowClick}
                            selectionsAsMap={selectionsAsMap}
                            focusAnchorRefs={focusAnchorRefs}
                            initialFocusedRowIndex={focusedRowIndexRef.current}
                            getRowAriaLabel={getRowAriaLabel}
                        />
                    </TableBody>
                </Table>
            </Box>
            {columnConfigurationIsActive && (
                <Menu
                    id="column-customizer-menu"
                    anchorEl={columnCustomizerAnchorEl}
                    open={Boolean(columnCustomizerAnchorEl)}
                    onClose={(): void => setColumnCustomizerAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transitionDuration={100}
                    slotProps={{
                        paper: {
                            sx: {
                                width: 320,
                                maxHeight: 450,
                                overflow: 'auto',
                                borderRadius: C1BorderRadius.large,
                            },
                        },
                    }}
                >
                    <CustomColumnConfigurationUI
                        columns={canBeConfiguredCols}
                        selectedKeys={thisTableSavedColConfig || defaultColumnKeys}
                        onConfigChange={handleColumnConfigChange}
                        onReset={handleResetColumnConfig}
                        hasCustomConfig={!!thisTableSavedColConfig}
                    />
                </Menu>
            )}
        </TableContainer>
        {isError && <Box sx={{ borderRadius: 1, borderTopLeftRadius: 0, borderTopRightRadius: 0, border: 1, borderColor: 'error.light', borderTop: 0 }}>
            <APIError message={error?.message} code={error?.code} sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }} />
        </Box>}
        {onPageChange && !!data?.[0]?.length && variant === 'default' &&
            <Stack direction="row-reverse" alignItems="center" sx={C1TableReduxStyles.paginationWrapper}>
                <Stack direction="row" spacing={2} alignItems="center">
                    {isBackgroundLoading && <C1CircularProgress size={16} aria-label={intl.formatMessage({ id: 'table_loading_more', defaultMessage: 'Loading more data' })} />}
                    <C1TablePagination
                        page={page}
                        pageSize={pageSize}
                        data={data}
                        canLoadMore={canLoadMore}
                        isLoading={isBackgroundLoading}
                        onPageChange={onPageChange}
                        totalCount={_totalCount}
                        sx={C1TableReduxStyles.tablePagination as SxProps}
                    />
                </Stack>

                {/* Aria-live region for screen reader announcements of selection changes */}
                <Box
                    component="span"
                    sx={{ ...visuallyHidden }}
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {selections?.length
                        ? intl.formatMessage(
                            { id: 'table_selections_live_region', defaultMessage: '{count} items selected' },
                            { count: selections.length },
                        )
                        : ''}
                </Box>

                {(selections && setSelections && !extraFooterActions) ? (
                    <DefaultSelectionsControl<T> selections={selections} setSelections={setSelections} />
                ) : extraFooterActions}
            </Stack>
        }
    </ErrorBoundary>
}

type DefaultSelectionsControlProps<T> = {
    selections: T[]
    setSelections: (selections: T[]) => void
}

const DefaultSelectionsControl = <T, >({ selections, setSelections }: DefaultSelectionsControlProps<T>): React.ReactElement => {
    return <Stack direction="row" spacing={1} alignItems="center">
        <Typography>
            <FormattedMessage
                id="component_c1_table_selected"
                defaultMessage="{count} items selected"
                values={{ count: selections.length }}
            />
        </Typography>
        {selections.length > 0 && <C1Button onClick={(): void => { setSelections([]) }}>
            <FormattedMessage id="component_c1_table_clear_selections" defaultMessage="Clear selections" />
        </C1Button>}
    </Stack>
}
